import {useNavigate, useParams, Link} from 'react-router-dom';
import { useState, useEffect } from 'react';
import {get} from '../helpers/httpClient';
import { usePosterColors } from '../PosterContext';
import GalleryRenderer from '../helpers/renderer/galleryRenderer';
import InquiryFormRenderer from '../helpers/renderer/inquiryFormRenderer';
import PosterRenderer from '../helpers/renderer/posterRenderer';
import Catalog from './component/catalog';
import Article from './component/article';
import Gallery from './component/gallery';
import GalleryItem from './component/galleryItem';
import Poster from './component/poster';

const pageCache = {};

function App() {
    const params = useParams();
    const navigate = useNavigate();
    const { setPosterColors } = usePosterColors();

    const slug = params['*'] || '';
    const [page, setPage] = useState(pageCache[slug]?.page || null);
    const [breadcrumbs, setBreadcrumbs] = useState(pageCache[slug]?.breadcrumbs || []);

    const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 768);

    useEffect(() => {
        const handleResize = () => setIsLargeScreen(window.innerWidth > 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    useEffect(() => {
        const getPage = async () => {
            try {
                const response = await get('pages?slug=' + slug);
                if (response) {
                    pageCache[slug] = { page: response.page, breadcrumbs: response.breadcrumbs || [] };
                    setPage(response.page);
                    setBreadcrumbs(response.breadcrumbs || []);
                }
            } catch (error) {
                navigate('/404')
            }
        };

        getPage();
    }, [slug]);

    useEffect(() => {
        if (page?.title) {
            document.title = page.title;
        }
    }, [page?.title]);

    useEffect(() => {
        if (page?.type === 'poster') {
            document.body.classList.add('poster-page');
            const { overlayColor, textShadeColor } = page.extra || {};
            setPosterColors({ overlayColor, textShadeColor });
            return () => {
                document.body.classList.remove('poster-page');
                setPosterColors(null);
            };
        }
    }, [page?.type]);

    useEffect(() => {
        if (page?.type !== 'poster') return;
        const { background, backgroundVertical, backgroundType } = page.extra || {};
        if (backgroundType !== 'image') return;

        const links = [];
        const addLink = (href, media) => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = href;
            if (media) link.media = media;
            document.head.appendChild(link);
            links.push(link);
        };

        if (background && backgroundVertical) {
            addLink(background, '(orientation: landscape)');
            addLink(backgroundVertical, '(orientation: portrait)');
        } else if (background || backgroundVertical) {
            addLink(background || backgroundVertical);
        }

        return () => links.forEach(l => l.remove());
    }, [page?.extra?.background, page?.extra?.backgroundVertical, page?.extra?.backgroundType]);

    if (!page) return null;

    if (page.type === 'poster') {
        return <Poster info={page} />;
    }


    const components = page.components?.items || [];
    const layout = page.layout || 'full';
    const isSplit = layout !== 'full';
    const contentOnLeft = layout.startsWith('left-');
    const contentPct = isSplit ? parseInt(layout.split('-')[1], 10) : 100;
    const compPct = 100 - contentPct;

    const renderComponent = (comp, idx) => {
        if (comp.type === 'inquiry_form') return <InquiryFormRenderer key={idx} comp={comp} />;
        if (comp.type === 'poster') return <PosterRenderer key={idx} comp={comp} />;
        return <GalleryRenderer key={idx} comp={comp} />;
    };

    const contentNode = (
        <div className="d-flex justify-content-center">
            {page.type === 'article' && <Article info={page} />}
            {(page.type === 'catalog' || page.type === 'gallery') && <Catalog info={page} />}
            {page.type === 'galleryitem' && <GalleryItem info={page} isMobile={!isLargeScreen} />}
        </div>
    );

    // Find first spare order number — that slot is where content goes
    const occupied = new Set(components.map(c => c.order));
    let contentAt = 0;
    while (occupied.has(contentAt)) contentAt++;

    const stackItems = [
        ...components.map(c => ({ comp: c, order: c.order })),
        { isContent: true, order: contentAt },
    ].sort((a, b) => a.order - b.order);

    const renderStack = () => stackItems.map((it, idx) =>
        it.isContent
            ? <div key="content" className="page-center">{contentNode}</div>
            : <div key={idx}>{renderComponent(it.comp, idx)}</div>
    );

    const breadcrumbsNode = breadcrumbs.length > 1 && (
        <nav className="breadcrumbs">
            {breadcrumbs.map((crumb, idx) => {
                const path = new URL(crumb.url).pathname;
                const isLast = idx === breadcrumbs.length - 1;
                const isMobileHidden = idx < breadcrumbs.length - 2;
                return (
                    <span key={idx} className={isMobileHidden ? 'breadcrumb-hidden-mobile' : ''}>
                        {idx > 0 && <span className="breadcrumb-sep">›</span>}
                        {isLast
                            ? <span className="breadcrumb-current">{crumb.name}</span>
                            : <Link to={path}>{crumb.name}</Link>
                        }
                    </span>
                );
            })}
        </nav>
    );

    return (
        <div key={page.slug} className="page-container page-appear">
            <div className="page-layout">
                {breadcrumbsNode}

                {(!isSplit || !isLargeScreen) && (
                    <div className="page-stack">{renderStack()}</div>
                )}

                {isSplit && isLargeScreen && (
                    <div className="page-split-wrapper">
                        {contentOnLeft ? (
                            <>
                                <div className="page-split-content" style={{ flex: `0 0 ${contentPct}%` }}>{contentNode}</div>
                                {components.length > 0 && (
                                    <div className="page-split-components" style={{ flex: `0 0 ${compPct}%` }}>
                                        {components.map(renderComponent)}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {components.length > 0 && (
                                    <div className="page-split-components" style={{ flex: `0 0 ${compPct}%` }}>
                                        {components.map(renderComponent)}
                                    </div>
                                )}
                                <div className="page-split-content" style={{ flex: `0 0 ${contentPct}%` }}>{contentNode}</div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default App;